import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listAgentSeats, agentSeatErrorMessage, type AgentSeat } from "../api/extensions";
import {
  listQueueCampaigns,
  listQueueMemberships,
  setAgentQueueMemberships,
  type QueueCampaign,
  type QueueMembership,
} from "../api/queues";

/**
 * Agents × queues, as checkboxes. One decision per cell, saved immediately.
 *
 * This is the supervisor's "move Nelson from inbound to outbound": untick one
 * column, tick the other, done. Each save sends the seat's FULL wanted set to
 * PUT /queues/agents/{id}/memberships, so the table always ends up matching
 * what is on screen rather than drifting by one toggle.
 *
 * A membership Asterisk will not resolve (the realtime `members` view refuses
 * a soft-deleted seat, or one with call forwarding set) is shown, not hidden:
 * the box stays ticked, the cell goes red, and the reason is in the title.
 * Hiding it would make the assignment look like it silently vanished.
 */

export const QUEUE_KEYS = {
  campaigns: ["queue-campaigns"] as const,
  memberships: ["queue-memberships"] as const,
};

/** Controlled checkbox list of queues for ONE seat. Shared by the matrix's
 * single-agent modal on My Agents and the Add-agent form, so the two never
 * describe a queue differently. */
export function QueueCampaignPicker({
  campaigns,
  value,
  onChange,
  disabled,
  invalidByCampaign,
}: {
  campaigns: QueueCampaign[];
  value: Set<number>;
  onChange: (next: Set<number>) => void;
  disabled?: boolean;
  invalidByCampaign?: Map<number, string>;
}) {
  if (campaigns.length === 0) {
    return (
      <p className="text-xs text-gray-500">
        No campaigns to assign yet — create a campaign first.
      </p>
    );
  }
  return (
    <div className="border border-gray-200 rounded max-h-48 overflow-auto divide-y divide-gray-100">
      {campaigns.map((c) => {
        const reason = invalidByCampaign?.get(c.campaign_id);
        return (
          <label
            key={c.campaign_id}
            className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
            title={reason}
          >
            <input
              type="checkbox"
              aria-label={c.name}
              disabled={disabled}
              checked={value.has(c.campaign_id)}
              onChange={(e) => {
                const next = new Set(value);
                if (e.target.checked) next.add(c.campaign_id);
                else next.delete(c.campaign_id);
                onChange(next);
              }}
            />
            <span className={reason ? "text-red-700" : ""}>{c.name}</span>
            <span className="ml-auto text-[11px] text-gray-400">
              {c.valid_members} agent{c.valid_members === 1 ? "" : "s"}
            </span>
          </label>
        );
      })}
    </div>
  );
}

function membershipsBySeat(rows: QueueMembership[]): Map<number, QueueMembership[]> {
  const m = new Map<number, QueueMembership[]>();
  for (const r of rows) {
    const list = m.get(r.extension_id) ?? [];
    list.push(r);
    m.set(r.extension_id, list);
  }
  return m;
}

export function QueueAssignmentMatrix() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const seats = useQuery({ queryKey: ["agent-seats"], queryFn: listAgentSeats });
  const campaigns = useQuery({ queryKey: QUEUE_KEYS.campaigns, queryFn: listQueueCampaigns });
  const memberships = useQuery({ queryKey: QUEUE_KEYS.memberships, queryFn: listQueueMemberships });

  const bySeat = useMemo(() => membershipsBySeat(memberships.data ?? []), [memberships.data]);

  const save = useMutation({
    mutationFn: ({ extensionId, campaignIds }: { extensionId: number; campaignIds: number[] }) =>
      setAgentQueueMemberships(extensionId, campaignIds),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: QUEUE_KEYS.memberships });
      qc.invalidateQueries({ queryKey: QUEUE_KEYS.campaigns });
      qc.invalidateQueries({ queryKey: ["queues", "live"] });
    },
    onError: (e) => setError(agentSeatErrorMessage(e)),
  });

  function toggle(seat: AgentSeat, campaignId: number, checked: boolean) {
    const current = new Set((bySeat.get(seat.extension_id) ?? []).map((m) => m.campaign_id));
    if (checked) current.add(campaignId);
    else current.delete(campaignId);
    save.mutate({ extensionId: seat.extension_id, campaignIds: [...current] });
  }

  const busySeat = save.isPending ? save.variables?.extensionId : null;
  const rows = seats.data?.seats ?? [];
  const cols = campaigns.data ?? [];

  if (seats.isLoading || campaigns.isLoading || memberships.isLoading) {
    return <p className="text-sm text-gray-500">Loading agents and queues…</p>;
  }
  if (seats.isError || campaigns.isError || memberships.isError) {
    return (
      <p className="text-sm text-red-600">
        {agentSeatErrorMessage(seats.error ?? campaigns.error ?? memberships.error)}
      </p>
    );
  }
  if (rows.length === 0) {
    return <p className="text-sm text-gray-500">No agent seats yet — add one under My Agents.</p>;
  }
  if (cols.length === 0) {
    return <p className="text-sm text-gray-500">No campaigns to assign agents to yet.</p>;
  }

  return (
    <div>
      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Agent</th>
              {cols.map((c) => (
                <th key={c.campaign_id} className="px-3 py-2 text-center whitespace-nowrap">
                  {c.name}
                  <div className="text-[10px] font-normal text-gray-400">
                    {c.valid_members} taking calls
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((seat) => {
              const mine = bySeat.get(seat.extension_id) ?? [];
              const byCampaign = new Map(mine.map((m) => [m.campaign_id, m]));
              return (
                <tr key={seat.extension_id}>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span className="font-medium">{seat.title || `Agent ${seat.name}`}</span>
                    <span className="ml-2 font-mono text-xs text-gray-500">{seat.name}</span>
                  </td>
                  {cols.map((c) => {
                    const m = byCampaign.get(c.campaign_id);
                    const invalid = m && !m.valid;
                    return (
                      <td
                        key={c.campaign_id}
                        className={`px-3 py-2 text-center ${invalid ? "bg-red-50" : ""}`}
                        title={invalid ? `Assigned, but Asterisk will not ring this agent: ${m.invalid_reason}` : undefined}
                      >
                        <input
                          type="checkbox"
                          aria-label={`${seat.title || seat.name} in ${c.name}`}
                          checked={!!m}
                          disabled={busySeat === seat.extension_id}
                          onChange={(e) => toggle(seat, c.campaign_id, e.target.checked)}
                        />
                        {invalid && (
                          <div className="text-[10px] text-red-700 leading-tight mt-0.5">
                            not usable
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">
        Changes take effect on the next call the queue offers. A red cell is an assignment the
        phone system will skip — hover it for the reason.
      </p>
    </div>
  );
}
